import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { listLscArchive, listLscLiveDashboard, listLscMatches, listLscMembers } from '../../api';
import { useLeagueHomeLivePolling } from '../../hooks/useLeagueHomeLivePolling';
import type { LscArchiveItem, LscLeague, LscLiveDashboardRow, LscMatch, LscMember } from '../../types';
import { selectMemberHomeLiveRows } from '../../stream/selectMemberHomeLiveRows';
import StreamLeagueHomeView from './StreamLeagueHome';

export type StreamLeagueHomePageProps = {
  leagueId: string;
  league: LscLeague;
  isCommissioner: boolean;
  isOperator: boolean;
  isOwner: boolean;
};

const ARCHIVE_FETCH_LIMIT = 24;

type LoadState = 'loading' | 'ready' | 'error';

function StreamHomeSkeleton() {
  return (
    <div
      data-testid="stream-home-skeleton"
      className="stream-scope flex flex-col gap-6"
      aria-busy="true"
    >
      <div className="h-64 rounded-stream-lg bg-stream-surface" />
      <div className="h-44 rounded-stream-lg bg-stream-surface" />
      <div className="h-44 rounded-stream-lg bg-stream-surface" />
    </div>
  );
}

const StreamLeagueHomePage: React.FC<StreamLeagueHomePageProps> = ({
  leagueId,
  league,
  isCommissioner,
  isOperator,
  isOwner,
}) => {
  const [liveRows, setLiveRows] = useState<LscLiveDashboardRow[]>([]);
  const [suppressArchiveFallback, setSuppressArchiveFallback] = useState(false);
  const [archives, setArchives] = useState<LscArchiveItem[]>([]);
  const [matches, setMatches] = useState<LscMatch[]>([]);
  const [members, setMembers] = useState<LscMember[]>([]);
  const [archiveState, setArchiveState] = useState<LoadState>('loading');
  const [liveState, setLiveState] = useState<LoadState>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedLiveStreamKey, setSelectedLiveStreamKey] = useState<string | null>(null);
  const [selectedArchive, setSelectedArchive] = useState<LscArchiveItem | null>(null);
  const liveSettled = useRef(false);
  const [params] = useSearchParams();
  const memberPreview = params.get('as') === 'member';

  const loadLive = useCallback(async () => {
    try {
      const dashboard = await listLscLiveDashboard(leagueId);
      setLiveRows(selectMemberHomeLiveRows(dashboard));
      setSuppressArchiveFallback(Boolean(dashboard.suppress_archive_fallback));
      setLiveState('ready');
      liveSettled.current = true;
    } catch {
      if (!liveSettled.current) {
        setLiveRows([]);
        setLiveState('error');
      }
    }
  }, [leagueId]);

  useEffect(() => {
    liveSettled.current = false;
    setLiveState('loading');
    void loadLive();
  }, [loadLive, reloadKey, memberPreview]);

  useLeagueHomeLivePolling(loadLive, liveRows.length > 0);

  useEffect(() => {
    let cancelled = false;
    setArchiveState('loading');
    listLscArchive(leagueId, {
      archive_status: 'published',
      limit: ARCHIVE_FETCH_LIMIT,
      group: 'match',
    })
      .then((data) => {
        if (cancelled) return;
        setArchives(data);
        setArchiveState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setArchives([]);
        setArchiveState('error');
      });
    listLscMatches(leagueId, { scope: 'active' })
      .then((data) => {
        if (!cancelled) setMatches(data);
      })
      .catch(() => {
        if (!cancelled) setMatches([]);
      });
    listLscMembers(leagueId)
      .then((data) => {
        if (!cancelled) setMembers(data.members);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId, reloadKey, memberPreview]);

  const settled = archiveState !== 'loading' && liveState !== 'loading';
  const loadFailed = archiveState === 'error' || liveState === 'error';

  if (!settled) {
    return <StreamHomeSkeleton />;
  }

  return (
    <>
      {loadFailed ? (
        <div
          data-testid="stream-home-load-error"
          role="alert"
          className="stream-scope mb-4 flex flex-wrap items-center gap-3 rounded-stream-lg bg-stream-surface p-4"
        >
          <p>Couldn&apos;t load games.</p>
          <button
            type="button"
            className="min-h-11 rounded-stream-md bg-stream-ink px-4 font-stream-display text-sm font-bold uppercase text-stream-ground"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Retry
          </button>
        </div>
      ) : null}
      <StreamLeagueHomeView
        leagueId={leagueId}
        league={league}
        isCommissioner={isCommissioner}
        isOperator={isOperator}
        isOwner={isOwner}
        liveRows={liveRows}
        suppressArchiveFallback={suppressArchiveFallback}
        archives={archives}
        matches={matches}
        members={members}
        loadFailed={loadFailed}
        selectedLiveStreamKey={selectedLiveStreamKey}
        onSelectLive={setSelectedLiveStreamKey}
        selectedArchive={selectedArchive}
        onSelectArchive={setSelectedArchive}
      />
    </>
  );
};

export default StreamLeagueHomePage;
